import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  RecordingsMainContent,
  RecordingsSidebar,
  useRecordingsWithPagination,
} from '@unsw/features/recordings'
import { useSidebar } from '@unsw/hooks'
import { useIsMobile } from '@unsw/hooks'
import { getUserRole } from '@unsw/hooks'
import { cn } from '@unsw/ui/lib/utils'

export const Route = createFileRoute('/dashboard/recordings')({
  beforeLoad: () => {
    const { isTeacher, isAdmin } = getUserRole()
    if (isTeacher || isAdmin) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: RecordingsPage,
})

function RecordingsPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { sidebarCollapsed, toggleSidebar, closeSidebar } = useSidebar()

  const { recordings, isLoading, hasMore, isLoadingMore, loadMore } = useRecordingsWithPagination()

  const handleSelectRecording = (id: number) => {
    navigate({
      to: '/dashboard/summary',
      search: { recordingId: id },
    })
  }

  // Treat undefined as desktop (false) for SSR safety
  const isMobileView = isMobile === true

  return (
    <div
      className={cn(
        'h-[calc(100vh-64px)] flex relative',
        // Desktop: with gap
        'md:gap-6',
        // Mobile: no gap
        'gap-0',
      )}
    >
      {/* Mobile: Backdrop (only visible when expanded) */}
      {isMobileView && (
        <button
          type="button"
          className={cn(
            'fixed top-[64px] inset-x-0 bottom-0 bg-black/50 z-30 transition-opacity duration-300',
            !sidebarCollapsed ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar - CSS-first for initial render, JS for interactions */}
      <div
        className={cn(
          'flex-shrink-0 bg-white z-40 transition-[width] duration-300 ease-in-out',
          // CSS-first: mobile gets narrow, desktop gets wide (correct on initial render)
          'w-14 border-r border-black/10',
          'md:w-80 md:rounded-lg md:border md:shadow-sm md:border-r-0',
          // JS override: mobile expanded state (only when confirmed mobile + expanded)
          isMobileView && !sidebarCollapsed && '!w-72 shadow-xl',
        )}
      >
        <RecordingsSidebar
          recordings={recordings}
          selectedRecordingId={null}
          onSelectRecording={handleSelectRecording}
          isLoading={isLoading}
          hideNewRecordingButton
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
          isCollapsed={isMobile !== false && sidebarCollapsed}
          onToggle={toggleSidebar}
        />
      </div>

      {/* Main Content - Welcome + Record + Recordings Grid */}
      <main
        className={cn(
          'flex-1 bg-white overflow-hidden',
          // Desktop: with border and rounded corners
          'md:rounded-lg md:border md:border-black/10 md:shadow-sm',
        )}
      >
        <RecordingsMainContent
          recordings={recordings}
          isLoading={isLoading}
          onSelectRecording={handleSelectRecording}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
        />
      </main>
    </div>
  )
}
